import {toggleBookmark} from '../modules/bookmarkToggle.js';

/**
 * BookmarkButton component - manages a single bookmark button element.
 * Handles toggling bookmark state for any Bookmarkable model.
 *
 * @example
 * const button = document.querySelector('.js-bookmarkButton');
 * const bookmarkButton = new BookmarkButton(button);
 */
export class BookmarkButton {
    #button;
    #bookmarkableType;
    #bookmarkableId;

    /**
     * @param {HTMLButtonElement} buttonElement - The button element to manage
     */
    constructor(buttonElement) {
        if (!(buttonElement instanceof HTMLElement)) {
            throw new Error('BookmarkButton requires a valid HTMLElement');
        }

        this.#button = buttonElement;
        this.#bookmarkableType = buttonElement.dataset.bookmarkableType;
        this.#bookmarkableId = buttonElement.dataset.bookmarkableId;

        // Validate required data attributes
        if (!this.#bookmarkableType || !this.#bookmarkableId) {
            throw new Error('BookmarkButton: Missing required data attributes');
        }

        this.#attachEventListener();
    }

    /** Attach click event listener to the button. */
    #attachEventListener() {
        this.#button.addEventListener('click', this.#handleClick.bind(this));
    }

    /**
     * Handle click event on the bookmark button
     * @param {Event} event
     */
    async #handleClick(event) {
        event.preventDefault();

        const currentlyBookmarked = this.#button.dataset.isBookmarked === 'true';
        const newState = await toggleBookmark(this.#button, this.#bookmarkableType, this.#bookmarkableId, currentlyBookmarked);

        if (newState !== null) {
            this.markAsBookmarked(newState);
        }
    }

    /**
     * Mark all bookmark buttons for a specific bookmarkable item as bookmarked/not bookmarked.
     * @param {boolean} isBookmarked - Whether the item is bookmarked
     */
    markAsBookmarked(isBookmarked) {
        const buttons = document.querySelectorAll(`.js-bookmarkButton[data-bookmarkable-type="${CSS.escape(this.#bookmarkableType)}"][data-bookmarkable-id="${CSS.escape(this.#bookmarkableId)}"]`);
        buttons.forEach((button) => this.#updateButtonState(/** @type {HTMLElement} */ (button), isBookmarked));

        // Update any associated text elements
        const textElements = document.querySelectorAll(`.js-bookmarkButtonText[data-bookmarkable-type="${CSS.escape(this.#bookmarkableType)}"][data-bookmarkable-id="${CSS.escape(this.#bookmarkableId)}"]`);
        textElements.forEach((textElement) => this.#updateTextElement(/** @type {HTMLElement} */ (textElement), isBookmarked));
    }

    /**
     * Update the text content of an associated bookmark text element.
     * @param {HTMLElement} textElement
     * @param {boolean} isBookmarked
     */
    #updateTextElement(textElement, isBookmarked) {
        const defaultText = textElement.dataset.textDefault;
        const bookmarkedText = textElement.dataset.textBookmarked;

        if (defaultText && bookmarkedText) {
            textElement.textContent = isBookmarked ? bookmarkedText : defaultText;
        }
    }

    /**
     * Update the state of a bookmark button.
     * @param {HTMLElement} button
     * @param {boolean} isBookmarked
     */
    #updateButtonState(button, isBookmarked) {
        // Update data attribute
        button.dataset.isBookmarked = isBookmarked.toString();

        // Update aria attributes
        button.setAttribute('aria-label', isBookmarked ? button.dataset.labelBookmarked : button.dataset.labelDefault);
        button.setAttribute('aria-pressed', isBookmarked.toString());

        // Update icon color
        const icon = button.querySelector('.svg-icon');
        if (icon) {
            icon.classList.toggle('fg-graphic-brand-default', isBookmarked);
        }
    }
}
